import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Link as LinkMui, Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { Chips } from 'components/atoms/Chips';
import { ELicencePrintStatus } from 'constants/LicencePrintStatus';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { licencePrintStatusVariant } from './helpers';
import { ERouteLinks } from 'models/route';
import { EPermission } from 'models/permissions';

interface IPrintoutTableCard {
  data: ITableDataSource;
  handleCancel: (id: string, version: number) => void;
  handleRetry: (id: string) => void;
}

export const PrintoutTableCard = ({ data, handleCancel, handleRetry }: IPrintoutTableCard) => {
  const { t } = useTranslations();
  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
      }}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.printoutId.label')}
        </Typography>
        <Typography variant="body2">{data.printoutId}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licenceNumber.label')}
        </Typography>
        <LinkMui
          href={ERouteLinks.DetailLicence.replace(
            ':licenceId',
            data.licenceUniqueId as string,
          )}
          underline="none"
          target="_blank"
          rel="noopener noreferrer"
        >
          {data.licenceNumber}
        </LinkMui>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licenceOwnerName.label')}
        </Typography>
        <LinkMui
          href={`${ERouteLinks.ViewLegalEntity.replace(
            ':id',
            data.licenceOwnerUniqueId as string,
          )}?type=${data.licenceOwnerType}`}
          underline="none"
          target="_blank"
          rel="noopener noreferrer"
        >
          {data.licenceOwner}
        </LinkMui>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.status.label')}
        </Typography>
        <Chips text={data.status} variant={licencePrintStatusVariant(data.statusUniqueId)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.printType.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.type)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.createDate.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.createdDate)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.printDate.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.printDate)}</Typography>
      </Grid>

      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          rowGap: 1,
          width: '100%',
          boxSizing: 'border-box',
          mt: 2,
          px: 2,
          py: 2,
          borderTop: `1px solid ${colors.primary100}`,
        }}
      >
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('licences.actions.label')}:{' '}
          </Typography>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}
        >
          {data.statusUniqueId === ELicencePrintStatus.WAITING && (
            <Button
              id={`printouts_card_cancelRequest-button-${data.id}`}
              variant={EButtonVariants.outlined}
              size={EButtonSizes.small}
              label={t('licences.cancel.label')}
              onClick={() => handleCancel(data.id as string, data.version)}
              permission={[EPermission.PRINT_LICENCE]}
            />
          )}
          {data.canRetry && (
            <Button
              variant={EButtonVariants.outlined}
              label={t('licences.retry.label')}
              onClick={() => handleRetry(data.id as string)}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
